import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCurrentUser } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';

// ============== TYPES ==============

export interface RawMaterialMovement {
  id: string;
  raw_material_id: string;
  movement_type: 'in' | 'out' | 'adjustment';
  quantity: number;
  previous_stock: number;
  new_stock: number;
  previous_square_meters?: number;
  new_square_meters?: number;
  square_meters_change?: number;
  reason: string;
  notes: string | null;
  reference_type: string | null;
  reference_id: string | null;
  performed_by: string | null;
  batch_number: string | null;
  roll_number: string | null;
  created_at: string;
  // From joins
  performed_by_profile?: {
    id: string;
    full_name: string;
  };
}

export interface RawMaterialCategory {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

// ============== CATEGORY QUERIES ==============

export function useRawMaterialCategories() {
  return useQuery({
    queryKey: ['raw-material-categories'],
    queryFn: async () => {
      const { data, error } = await api
        .from('raw_material_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as RawMaterialCategory[];
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (name: string) => {
      // Get max sort_order
      const { data: maxOrder } = await api
        .from('raw_material_categories')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextOrder = (maxOrder?.[0]?.sort_order || 0) + 1;

      const { data, error } = await api
        .from('raw_material_categories')
        .insert({
          name,
          description: name,
          is_system: false,
          sort_order: nextOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-material-categories'] });
      toast({
        title: 'Categoría creada',
        description: 'La nueva categoría está disponible',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la categoría',
        variant: 'destructive',
      });
    },
  });
}

// ============== MOVEMENT QUERIES ==============

export function useRawMaterialMovements(materialId: string | undefined) {
  return useQuery({
    queryKey: ['raw-material-movements', materialId],
    queryFn: async () => {
      if (!materialId) return [];

      const { data, error } = await api
        .from('raw_material_movements')
        .select(`
          *,
          performed_by_profile:profiles!raw_material_movements_performed_by_fkey(id, full_name)
        `)
        .eq('raw_material_id', materialId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as RawMaterialMovement[];
    },
    enabled: !!materialId,
  });
}

// ============== MOVEMENT MUTATIONS ==============

export function useRegisterRawMaterialMovement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      materialId: string;
      movementType: 'in' | 'out' | 'adjustment';
      quantity: number;
      squareMetersChange?: number;
      reason: string;
      notes?: string;
      currentStock: number;
      currentSquareMeters?: number;
      batchNumber?: string;
      rollNumber?: string;
    }) => {
      const user = getCurrentUser();

      // Calculate new values
      let newStock: number;
      let movementQuantity: number;
      let newSquareMeters: number | undefined;
      let sqmChange: number | undefined;

      if (data.movementType === 'in') {
        newStock = data.currentStock + data.quantity;
        movementQuantity = data.quantity;
        if (data.currentSquareMeters !== undefined && data.squareMetersChange !== undefined) {
          newSquareMeters = data.currentSquareMeters + data.squareMetersChange;
          sqmChange = data.squareMetersChange;
        }
      } else if (data.movementType === 'out') {
        newStock = data.currentStock - data.quantity;
        movementQuantity = -data.quantity;
        if (data.currentSquareMeters !== undefined && data.squareMetersChange !== undefined) {
          newSquareMeters = data.currentSquareMeters - data.squareMetersChange;
          sqmChange = -data.squareMetersChange;
        }
      } else {
        // Adjustment - quantity is the new total
        newStock = data.quantity;
        movementQuantity = data.quantity - data.currentStock;
        if (data.currentSquareMeters !== undefined && data.squareMetersChange !== undefined) {
          newSquareMeters = data.squareMetersChange;
          sqmChange = data.squareMetersChange - data.currentSquareMeters;
        }
      }

      // Insert movement record
      const { error: movementError } = await api
        .from('raw_material_movements')
        .insert({
          raw_material_id: data.materialId,
          movement_type: data.movementType,
          quantity: movementQuantity,
          previous_stock: data.currentStock,
          new_stock: newStock,
          previous_square_meters: data.currentSquareMeters,
          new_square_meters: newSquareMeters,
          square_meters_change: sqmChange,
          reason: data.reason,
          notes: data.notes || null,
          performed_by: user?.id,
          batch_number: data.batchNumber || null,
          roll_number: data.rollNumber || null,
        });

      if (movementError) throw movementError;

      // Update raw material stock
      const updatePayload: Record<string, any> = {
        quantity: newStock,
        updated_at: new Date().toISOString(),
      };

      if (newSquareMeters !== undefined) {
        updatePayload.square_meters = newSquareMeters;
      }

      if (data.movementType === 'in') {
        updatePayload.purchase_date = new Date().toISOString().split('T')[0];
      }

      const { error: updateError } = await api
        .from('raw_materials')
        .update(updatePayload)
        .eq('id', data.materialId);

      if (updateError) throw updateError;

      return { newStock, newSquareMeters };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['raw-material-movements', variables.materialId] });
      queryClient.invalidateQueries({ queryKey: ['raw_materials'] });

      const typeLabels = {
        in: 'Entrada',
        out: 'Salida',
        adjustment: 'Ajuste',
      };

      toast({
        title: 'Movimiento registrado',
        description: `${typeLabels[variables.movementType]} de ${Math.abs(variables.quantity)} unidades`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al registrar movimiento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============== HELPER FUNCTIONS ==============

export function getMovementTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    in: 'Entrada',
    out: 'Salida',
    adjustment: 'Ajuste',
  };
  return labels[type] || type;
}

export function getMovementTypeColor(type: string): string {
  const colors: Record<string, string> = {
    in: 'text-green-600 bg-green-100',
    out: 'text-red-600 bg-red-100',
    adjustment: 'text-blue-600 bg-blue-100',
  };
  return colors[type] || 'text-gray-600 bg-gray-100';
}

export function getRawMaterialReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    purchase: 'Compra',
    return: 'Devolución',
    transfer_in: 'Transferencia entrada',
    production_return: 'Retorno de producción',
    initial: 'Stock inicial',
    production: 'Producción',
    consumption: 'Consumo',
    damage: 'Material dañado',
    expired: 'Vencido',
    transfer_out: 'Transferencia salida',
    sample: 'Muestra',
    correction: 'Corrección de inventario',
    recount: 'Reconteo físico',
    system_error: 'Error de sistema',
    other: 'Otro',
  };
  return labels[reason] || reason;
}
